import {
  TOPIC_CONTENT_SOURCES,
  type TopicContentSource,
} from "./topic-content-sources";
import { TOPIC_GUIDES, type TopicGuide } from "./topic-guides";

export const CAR_REGISTRATION_GUIDE = {
  topicId: "car-registration",
  locale: "ko",
  publicationStatus: "korean_test_publish",
  risk: "R2",
  checkedAt: "2026-07-16",
  jurisdictionLabel: "NSW 차량 등록·CTP·검사 절차",
  summary:
    "NSW에서 중고차를 사기 전에는 번호판으로 무료 rego check를 하고, private sale이라면 VIN으로 PPSR search를 별도로 확인하세요. 구매 후에는 판매자의 Notice of Disposal 여부를 확인하고 14일 안에 registration을 본인 명의로 이전합니다. 갱신할 때는 renewal notice에 맞는 CTP 기간과 필요한 pink slip을 준비하고, 등록이 만료되면 제한된 예외나 허가 없이 운전하지 마세요. CTP는 사고 인명피해를 위한 의무보험이며 차량·재산 수리비를 보장하는 보험은 아닙니다.",
  keyPoints: [
    "무료 NSW rego check와 유료 PPSR vehicle search는 확인하는 내용이 다르므로 둘 다 검토하세요.",
    "구매자는 차량을 취득한 날부터 14일 안에 registration transfer를 완료해야 late fee를 피할 수 있습니다.",
    "판매자는 매각 직후 Notice of Disposal을 제출하고 영수증과 사본을 보관하세요.",
    "current NSW registration을 이전할 때 CTP는 자동 이전되며 일반적으로 별도 pink slip은 필요하지 않습니다.",
    "갱신은 renewal notice에 표시된 inspection 요구와 CTP 기간을 맞춰 준비하세요.",
    "대부분의 5년 초과 light vehicle은 연간 safety check가 필요할 수 있고 report는 6개월간 유효합니다.",
    "CTP는 인명피해 제도이며 본인 차량이나 다른 재산의 수리비를 보장하지 않습니다.",
    "등록이 만료된 차량은 원칙적으로 운전할 수 없으며 3개월 초과 만료 시 re-registration과 AUVIS가 필요할 수 있습니다.",
  ],
  sections: [
    {
      id: "check-before-buying",
      title: "1. 돈을 보내기 전에 rego·VIN·seller 기록을 확인하세요",
      body: [
        "차량의 NSW number plate로 Service NSW 무료 registration check를 실행해 expiry, suspended·cancelled 상태, restrictions, concessions, CTP provider·expiry와 available odometer readings를 확인하세요. 화면 결과를 저장하고 차량의 plate, VIN, engine number와 판매자가 제시한 서류가 일치하는지 직접 대조하세요.",
        "private seller에게 구매한다면 PPSR에서 VIN 기준 used vehicle search를 별도로 실행하세요. PPSR는 차량에 등록된 security interest와 debt risk를 확인하는 정부 register이며 stolen 또는 written-off 정보도 표시될 수 있습니다. rego가 유효하다는 사실만으로 finance owing이 없다고 판단하지 마세요.",
      ],
      checklist: [
        "NSW number plate와 VIN·engine number",
        "무료 rego check 결과와 expiry date",
        "suspended·cancelled·restriction·concession 표시",
        "PPSR search certificate와 검색 시각",
        "seller 이름·licence 또는 dealer licence 정보",
        "purchase price, market value와 서면 receipt",
        "독립적인 mechanical·body inspection 필요 여부",
      ],
      sourceIds: [
        "car-registration-source-rego-check",
        "car-registration-source-ppsr",
      ],
    },
    {
      id: "transfer-after-purchase",
      title: "2. 판매자의 NOD를 확인하고 14일 안에 명의이전하세요",
      body: [
        "current NSW-registered vehicle을 구매하면 판매자가 Notice of Disposal을 제출하고 구매자가 acquisition 후 14일 안에 registration을 이전해야 합니다. 판매자가 NOD를 제출한 개인 거래라면 MyServiceNSW Account에서 온라인 transfer가 가능할 수 있으며, organisation·interstate seller·사망자 차량·일부 special plate·3개월 초과 만료 차량 등은 Service NSW Centre 절차가 필요합니다.",
        "purchase date와 purchase price 또는 market value 중 높은 금액, concession 정보와 transfer fee·stamp duty를 준비하세요. current NSW registration을 이전할 때 CTP green slip은 자동으로 새 operator에게 이전되고 safety inspection report는 필요하지 않습니다. 다만 CTP 외의 comprehensive·third party property 보험은 자동 이전된다고 가정하지 말고 운전 전에 별도로 준비하세요.",
      ],
      checklist: [
        "판매자의 Notice of Disposal 제출 확인",
        "purchase date와 receipt·proof of entitlement",
        "purchase price 또는 market value 중 높은 금액",
        "MyServiceNSW Account와 identity·concession 자료",
        "transfer fee·stamp duty·추가 tax 예상액",
        "special plate·interstate·organisation 등 centre 방문 사유",
        "CTP 외 차량·재산보험 시작일",
      ],
      sourceIds: [
        "car-registration-source-transfer",
        "car-registration-source-ctp",
      ],
    },
    {
      id: "renew-registration",
      title: "3. renewal notice에 맞춰 CTP·inspection·payment를 순서대로 준비하세요",
      body: [
        "renewal notice에서 expiry date, registration term, CTP와 safety inspection 필요 여부를 확인하세요. 별도 CTP가 필요한 차량은 renewal term과 같은 기간의 green slip을 구매해야 하며 insurer가 정보를 Transport for NSW에 전송했는지 확인하세요. 6개월 renewal을 선택하는 경우 expiry 후 21일 안에 CTP와 registration payment를 모두 완료해야 하는 조건이 적용됩니다.",
        "대부분의 5년 초과 light vehicle은 notice에 따라 AIS station의 annual safety check, 즉 pink slip이 필요합니다. NSW inspection report는 일반적으로 발급일부터 6개월 동안 유효합니다. 검사에 실패하면 수리 후 통과해야 갱신할 수 있으며 같은 AIS station에서 14일 안에 재검사를 받는 경우 추가 inspection fee가 면제될 수 있습니다.",
        "payment receipt와 갱신 결과를 직접 확인하세요. BPAY는 처리에 2~3 business days가 걸릴 수 있고 Transport for NSW가 full payment를 받기 전에는 registration이 renewed되지 않습니다.",
      ],
      checklist: [
        "renewal notice와 expiry date",
        "선택한 registration term과 같은 기간의 CTP",
        "notice의 pink slip 요구 여부",
        "AIS inspection date·result·6개월 유효기간",
        "수리·14일 재검사 기록",
        "payment method별 처리시간과 receipt",
        "MyServiceNSW에서 최종 renewed status 확인",
      ],
      sourceIds: [
        "car-registration-source-renew",
        "car-registration-source-inspections",
        "car-registration-source-ctp",
      ],
    },
    {
      id: "expired-or-insurance-problem",
      title: "4. 만료·무등록·보험 공백이 생기면 운전하지 말고 합법 경로를 확인하세요",
      body: [
        "expiry date까지 갱신하지 않으면 차량은 unregistered 상태가 됩니다. NSW에서는 매우 제한된 registration 관련 이동 예외나 valid unregistered vehicle permit이 없는 한 unregistered vehicle 운전이 불법입니다. 등록·CTP 상태를 추측하지 말고 Service NSW check로 확인한 뒤, 필요한 경우 tow·inspection appointment·permit·re-registration 경로를 문의하세요.",
        "registration이 3개월 넘게 만료된 차량은 online renewal이 아니라 re-registration이 필요하고, 일반적으로 AUVIS inspection(blue slip)이 요구됩니다. AUVIS는 safety뿐 아니라 design·identity 기준을 확인하며 report는 42일간 유효합니다.",
        "CTP green slip은 vehicle registration에 필요한 compulsory cover로 사고로 인한 사람의 injury·death 관련 제도입니다. 본인 차량, 상대 차량 또는 다른 property의 repair·replacement 비용은 CTP가 보장하지 않으므로 자신의 위험에 맞는 별도 motor insurance를 검토하세요.",
      ],
      checklist: [
        "현재 registration·CTP status와 expiry date",
        "운전하지 않고 차량을 이동할 tow 또는 permit 계획",
        "3개월 초과 만료 여부",
        "AUVIS inspection과 42일 report 유효기간",
        "Service NSW re-registration 서류와 비용",
        "CTP와 property damage 보험의 보장범위 구분",
        "사고·fine·defect notice가 있으면 관련 공식 도움",
      ],
      sourceIds: [
        "car-registration-source-renew",
        "car-registration-source-inspections",
        "car-registration-source-ctp",
      ],
    },
    {
      id: "sell-and-dispose",
      title: "5. 판매 즉시 Notice of Disposal과 차량 관련 계정을 정리하세요",
      body: [
        "NSW-registered vehicle을 팔거나 양도하면 판매자는 Notice of Disposal을 즉시 제출해야 합니다. NOD에는 buyer details, sale date와 sale price 또는 market value 중 높은 금액이 필요하며, online이 불가능한 organisation·interstate buyer·conditional registration 거래는 paper form 절차를 확인하세요.",
        "NOD receipt와 sale receipt를 보관하면 이후 buyer의 parking·driving fine 책임 문제를 줄이고 buyer가 14일 안에 transfer할 수 있습니다. 판매 후 toll account에서 plate를 제거하고 tag·permit을 회수하며 comprehensive insurance를 갱신·취소하세요. 차량의 CTP는 current registration과 함께 buyer에게 자동 이전됩니다.",
      ],
      checklist: [
        "buyer licence 또는 licensed dealer details",
        "sale date와 price·market value",
        "Notice of Disposal receipt·copy",
        "buyer에게 제공한 proof of sale",
        "toll account의 plate 삭제와 tag 회수",
        "comprehensive·property insurance 변경",
        "special plates 유지·교환·판매 여부",
      ],
      sourceIds: ["car-registration-source-disposal"],
    },
  ],
  faqs: [
    {
      id: "car-registration-rego-vs-ppsr",
      question: "무료 rego check만 하면 finance owing도 확인되나요?",
      answer:
        "아닙니다. NSW rego check는 등록·CTP·restriction 등을 확인합니다. private used vehicle의 debt 또는 security interest 위험은 VIN으로 PPSR search를 별도로 실행하고 certificate를 보관하세요.",
      sourceIds: [
        "car-registration-source-rego-check",
        "car-registration-source-ppsr",
      ],
    },
    {
      id: "car-registration-transfer-deadline",
      question: "중고차를 산 뒤 언제까지 명의이전해야 하나요?",
      answer:
        "current NSW registration 차량의 구매자는 취득 후 14일 안에 transfer해야 late transfer fee를 피할 수 있습니다. 판매자의 NOD가 제출됐는지 확인하고 online eligibility가 없으면 Service NSW Centre를 이용하세요.",
      sourceIds: ["car-registration-source-transfer"],
    },
    {
      id: "car-registration-no-nod",
      question: "판매자가 Notice of Disposal을 제출하지 않았습니다.",
      answer:
        "온라인 transfer가 불가능할 수 있습니다. signed registration certificate 또는 다른 proof of registration entitlement, proof of identity와 transfer form을 준비해 Service NSW Centre 절차를 확인하고 판매자에게 NOD 제출을 서면으로 요청하세요.",
      sourceIds: [
        "car-registration-source-transfer",
        "car-registration-source-disposal",
      ],
    },
    {
      id: "car-registration-pink-slip-transfer",
      question: "current NSW rego 중고차를 이전할 때 pink slip이 필요한가요?",
      answer:
        "Service NSW는 current NSW registration transfer에는 safety inspection report가 필요하지 않다고 안내합니다. 다만 registration이 오래 만료됐거나 interstate·cancelled vehicle이면 별도 inspection과 re-registration 절차가 적용될 수 있습니다.",
      sourceIds: [
        "car-registration-source-transfer",
        "car-registration-source-inspections",
      ],
    },
    {
      id: "car-registration-pink-slip-renewal",
      question: "모든 차량이 매년 pink slip을 받아야 하나요?",
      answer:
        "아닙니다. 대부분의 5년 초과 light vehicle은 annual safety check가 필요하지만 예외가 있고 최종 기준은 renewal notice입니다. notice와 registration term 변경에 따른 요구를 확인하세요.",
      sourceIds: [
        "car-registration-source-renew",
        "car-registration-source-inspections",
      ],
    },
    {
      id: "car-registration-bpay",
      question: "expiry 당일 BPAY로 내면 바로 갱신되나요?",
      answer:
        "아닐 수 있습니다. Service NSW는 BPAY 처리에 2~3 business days가 걸리고 full payment가 접수될 때까지 renewed되지 않는다고 안내합니다. expiry 전에 처리시간을 고려하고 완료 status를 확인하세요.",
      sourceIds: ["car-registration-source-renew"],
    },
    {
      id: "car-registration-expired-drive",
      question: "rego가 만료됐는데 검사소까지 운전해도 되나요?",
      answer:
        "무등록 차량 운전은 원칙적으로 불법이며 허용되는 registration-related journey나 UVP에는 조건이 있습니다. 자신의 이동이 예외라고 임의로 판단하지 말고 Transport for NSW에 route·purpose·permit 필요 여부를 확인하거나 tow를 이용하세요.",
      sourceIds: [
        "car-registration-source-renew",
        "car-registration-source-inspections",
      ],
    },
    {
      id: "car-registration-ctp-cover",
      question: "CTP green slip이 있으면 사고 차량 수리비도 보장되나요?",
      answer:
        "아닙니다. CTP는 motor crash의 injury·death 관련 compulsory scheme이며 차량이나 property repair·replacement 비용을 보장하지 않습니다. 별도 motor insurance의 보장범위를 확인하세요.",
      sourceIds: ["car-registration-source-ctp"],
    },
    {
      id: "car-registration-seller-fines",
      question: "차를 팔았는데 buyer의 fine이 제게 왔습니다.",
      answer:
        "Notice of Disposal receipt, sale receipt와 buyer details를 준비해 notice의 nomination·review 절차를 확인하세요. 앞으로는 매각 직후 NOD를 제출하고 toll account에서도 plate를 즉시 제거하세요.",
      sourceIds: ["car-registration-source-disposal"],
    },
  ],
  sourceIds: [
    "car-registration-source-rego-check",
    "car-registration-source-ppsr",
    "car-registration-source-transfer",
    "car-registration-source-disposal",
    "car-registration-source-renew",
    "car-registration-source-inspections",
    "car-registration-source-ctp",
  ],
  additionalChecks: [
    "차량 구매 당일 plate·VIN으로 최신 rego check와 PPSR search를 다시 실행하세요.",
    "transfer fee, stamp duty, motor vehicle tax와 concession은 신청 직전 공식 calculator에서 확인하세요.",
    "renewal notice의 CTP term, inspection 요구와 payment 처리시간을 expiry 전에 재확인하세요.",
    "interstate·imported·written-off·conditional·modified vehicle은 별도 registration 경로를 확인하세요.",
    "unregistered vehicle 이동은 목적·route·permit 조건을 Transport for NSW에 직접 확인하세요.",
    "CTP 외 comprehensive·third party property·fire and theft 보험의 시작·종료일을 별도 provider에서 확인하세요.",
  ],
  disclaimer:
    "이 안내는 NSW 일반 light vehicle의 구매·명의이전·갱신·판매와 CTP 구분에 관한 생활정보입니다. 차량의 ownership, roadworthiness, insurance claim, fine liability 또는 특정 registration 승인 여부를 판단하지 않습니다. 차량 종류·등록상태·거래 형태·개조·주간 이동과 개인 상황에 따라 서류·검사·비용이 달라질 수 있으므로 거래·운전·신청 전에 Service NSW, Transport for NSW, SIRA와 PPSR의 최신 공식 안내를 확인하세요.",
} as const satisfies TopicGuide;

export const CAR_REGISTRATION_SOURCES = [
  {
    sourceId: "car-registration-source-rego-check",
    topicId: "car-registration",
    title: "Check a vehicle registration",
    publisher: "Service NSW",
    url: "https://www.service.nsw.gov.au/transaction/check-a-vehicle-registration",
    trust: 5,
    jurisdiction: "nsw",
    checkedAt: "2026-07-16",
    verificationMethod:
      "2026년 3월 31일 게시된 Service NSW 본문에서 expiry, suspended·cancelled, restrictions, concessions, CTP와 odometer 정보 확인",
    reviewStatus: "live_verified",
    claimScope: "NSW vehicle의 무료 registration status check와 PPSR·history report 구분",
    followUp: "구매 직전 plate로 새 check를 실행하고 current result를 저장",
  },
  {
    sourceId: "car-registration-source-ppsr",
    topicId: "car-registration",
    title: "Personal Property Securities Register",
    publisher: "Australian Financial Security Authority",
    url: "https://www.ppsr.gov.au/",
    trust: 5,
    jurisdiction: "australia",
    checkedAt: "2026-07-16",
    verificationMethod:
      "Australian Government PPSR 공식 본문에서 used vehicle search, security interest·debt risk, stolen·written-off 표시와 current fee 확인",
    reviewStatus: "live_verified",
    claimScope: "private used vehicle 구매 전 VIN 기반 PPSR search와 certificate의 목적",
    followUp: "거래 직전 정확한 VIN으로 search하고 current fee·result explanation 재확인",
  },
  {
    sourceId: "car-registration-source-transfer",
    topicId: "car-registration",
    title: "Transfer a vehicle registration",
    publisher: "Service NSW",
    url: "https://www.service.nsw.gov.au/transaction/transfer-a-vehicle-registration",
    trust: 5,
    jurisdiction: "nsw",
    checkedAt: "2026-07-16",
    verificationMethod:
      "2026년 3월 16일 게시된 Service NSW 본문에서 seller NOD, buyer 14일, online·centre eligibility, fee·stamp duty, CTP 자동이전과 pink slip 면제 확인",
    reviewStatus: "live_verified",
    claimScope: "current NSW-registered vehicle ownership transfer의 기한·자료·비용과 예외",
    followUp: "실제 vehicle status와 transaction type에 따른 online eligibility·current fees 재확인",
  },
  {
    sourceId: "car-registration-source-disposal",
    topicId: "car-registration",
    title: "Submit a notice of disposal for a vehicle",
    publisher: "Service NSW",
    url: "https://www.service.nsw.gov.au/transaction/submit-a-notice-of-disposal-for-a-vehicle",
    trust: 5,
    jurisdiction: "nsw",
    checkedAt: "2026-07-16",
    verificationMethod:
      "2026년 3월 24일 게시된 Service NSW 본문에서 즉시 NOD, buyer·sale data, online·paper 조건, receipt, toll·insurance와 CTP 이전 확인",
    reviewStatus: "live_verified",
    claimScope: "NSW vehicle seller의 Notice of Disposal와 판매 후 기록·계정 정리",
    followUp: "buyer 유형·conditional registration·special plates에 따른 paper 또는 centre 절차 재확인",
  },
  {
    sourceId: "car-registration-source-renew",
    topicId: "car-registration",
    title: "Renew a vehicle registration online",
    publisher: "Service NSW",
    url: "https://www.service.nsw.gov.au/transaction/renew-a-vehicle-registration",
    trust: 5,
    jurisdiction: "nsw",
    checkedAt: "2026-07-16",
    verificationMethod:
      "2026년 2월 26일 게시된 Service NSW 본문에서 matching CTP term, inspection, 6개월 renewal 21일 조건, BPAY 처리, 3개월 초과 재등록과 무등록 위험 확인",
    reviewStatus: "live_verified",
    claimScope: "NSW registration renewal의 준비물·처리시간·inspection과 expiry 이후 절차",
    followUp: "renewal notice, registration term, current fees와 payment receipt를 실제 vehicle 기준으로 재확인",
  },
  {
    sourceId: "car-registration-source-inspections",
    topicId: "car-registration",
    title: "Safety checks and vehicle inspections",
    publisher: "Transport for NSW",
    url: "https://www.nsw.gov.au/driving-boating-and-transport/vehicle-registration/renewing-vehicle-registration/safety-checks-and-vehicle-inspections",
    trust: 5,
    jurisdiction: "nsw",
    checkedAt: "2026-07-16",
    verificationMethod:
      "NSW Government 본문에서 5년 초과 light vehicle pink slip, AIS, 6개월 validity, failed check 14일, AUVIS 대상과 42일 validity 확인",
    reviewStatus: "live_verified",
    claimScope: "NSW pink slip·AUVIS inspection 필요조건, validity와 재검사 절차",
    followUp: "renewal notice와 vehicle category에 표시된 actual inspection requirement 재확인",
  },
  {
    sourceId: "car-registration-source-ctp",
    topicId: "car-registration",
    title: "Compulsory Green Slip insurance",
    publisher: "State Insurance Regulatory Authority",
    url: "https://www.sira.nsw.gov.au/motor/compulsory-third-party-motor-vehicle-insurance-green-slips",
    trust: 5,
    jurisdiction: "nsw",
    checkedAt: "2026-07-16",
    verificationMethod:
      "SIRA 공식 본문에서 registration 전 CTP 의무, injury·death coverage, term과 property repair exclusion 확인",
    reviewStatus: "live_verified",
    claimScope: "NSW CTP Green Slip의 등록상 역할과 인명·재산 보장범위 구분",
    followUp: "차량·운전자별 quote, term, insurer와 claim coverage는 SIRA price check·provider에서 재확인",
  },
] as const satisfies readonly TopicContentSource[];

const mutableGuides = TOPIC_GUIDES as unknown as TopicGuide[];
if (!mutableGuides.some((guide) => guide.topicId === CAR_REGISTRATION_GUIDE.topicId)) {
  mutableGuides.push(CAR_REGISTRATION_GUIDE);
}

const mutableSources = TOPIC_CONTENT_SOURCES as unknown as TopicContentSource[];
for (const source of CAR_REGISTRATION_SOURCES) {
  if (!mutableSources.some((existing) => existing.sourceId === source.sourceId)) {
    mutableSources.push(source);
  }
}
